import { useMutation, useQuery } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { InvoiceData } from '@/types/api';

/**
 * Portal access to first-class invoices. Scoped and permission-gated
 * server-side: drafts are never returned, sent/partial invoices can be paid
 * via Stripe, and paid/void render read-only.
 */

export function usePortalInvoices() {
  return useQuery({
    queryKey: ['portal-invoices'],
    queryFn: async (): Promise<Paginated<InvoiceData>> => {
      const { data } = await api.get<Paginated<InvoiceData>>('/v1/portal/invoices');
      return data;
    },
  });
}

export function usePortalInvoice(id: number | undefined) {
  return useQuery({
    queryKey: ['portal-invoice', id],
    enabled: id != null,
    queryFn: async (): Promise<InvoiceData> => {
      const { data } = await api.get<InvoiceData>(`/v1/portal/invoices/${id}`);
      return data;
    },
  });
}

type PaymentIntentResponse = {
  client_secret: string;
  publishable_key: string;
  amount_cents: number;
  currency: string;
  can_save_card: boolean;
};

export function usePortalInvoicePaymentIntent(id: number) {
  return useMutation<PaymentIntentResponse, Error, boolean>({
    mutationFn: async (saveCard: boolean): Promise<PaymentIntentResponse> => {
      await primeCsrf();
      const { data } = await api.post<PaymentIntentResponse>(
        `/v1/portal/invoices/${id}/payment-intent`,
        { save_card: saveCard },
      );
      return data;
    },
  });
}
